/* Sekage intro hero (sections/sekage-scroll-hero.liquid).
   The inline script in the section decides the mode and locks the scroll: html.sk-hero-motion means the film
   plays. This file frames the film, shows the header group at the menu cue (data-menu-cue, the wordmark fade),
   glides the film from its intro framing to its final framing from the glide cue (data-glide-start) to the end,
   and, when the film ends, shows the hero copy and unlocks the page; the film then holds its last frame.
   A stalled film is caught by a watchdog. Without the motion class only the final framing is applied. */
(() => {
  const root = document.documentElement;
  const section = document.querySelector('[data-sk-scroll-hero]');
  if (!section) return;
  const video = section.querySelector('.sk-scroll-hero__video');
  const motion = () => root.classList.contains('sk-hero-motion');

  /* Framing. The film and poster boxes are set to the intro framing: a centred cover in landscape, 130% of the
     width centred in portrait, so the title sits dead centre. The final framing is a transform from there:
     - landscape: the wallet (WALLET, fractions of the frame height and width in the end pose) fits between the
       header group's bottom and the screen bottom, and on wide screens moves right to clear the copy column;
     - portrait: the same width, centred at 33% of the height, with the copy below.
     The rock and fog may crop; the wallet never does. Edges that leave the screen fade into black (CSS). */
  const WALLET = { top: 0.115, bottom: 0.78, left: 0.219, right: 0.766 };
  const PAD = 20;
  const GAP = 32;
  const stage = section.querySelector('.sk-scroll-hero__stage');
  const copy = section.querySelector('.sk-scroll-hero__copy');
  const media = [...section.querySelectorAll('.sk-scroll-hero__media')];
  const landscape = matchMedia('(orientation: landscape)');
  const px = name => parseFloat(getComputedStyle(root).getPropertyValue(name)) || 0;
  let finalTransform = 'none';
  let phase = motion() ? 'intro' : 'final'; // intro → glide → final

  const measure = () => {
    const r = stage.getBoundingClientRect();
    const top0 = r.top + scrollY;
    const w = r.width, h = r.height;
    // Intro framing.
    let bw, bh, bx, by;
    if (landscape.matches) { bh = Math.max(h, w * 9 / 16); bw = bh * 16 / 9; }
    else { bw = w * 1.3; bh = bw * 9 / 16; }
    bx = (w - bw) / 2; by = (h - bh) / 2;
    // Final framing.
    let fw, fh, fx, fy;
    if (landscape.matches) {
      const headerBottom = px('--sk-ann-h') + px('--sk-bar-h') - top0 + PAD;
      const screenBottom = Math.min(h, innerHeight - top0) - PAD;
      fh = Math.min(Math.max(h, w * 9 / 16), (screenBottom - headerBottom) / (WALLET.bottom - WALLET.top));
      // Where the wallet is too wide to sit right of the copy column, shrink it to fit (by up to a quarter).
      const c = copy.getBoundingClientRect();
      const copyRight = c.right - r.left + GAP;
      const room = w - PAD - copyRight;
      const fitH = room / ((WALLET.right - WALLET.left) * 16 / 9);
      if (fitH < fh && fitH >= fh * 0.75) fh = fitH;
      fw = fh * 16 / 9;
      fy = (headerBottom + screenBottom) / 2 - (WALLET.top + WALLET.bottom) / 2 * fh;
      fy = Math.min(Math.max(fy, Math.min(0, h - fh)), Math.max(0, h - fh));
      fy = Math.max(fy, headerBottom - WALLET.top * fh);
      fy = Math.min(fy, screenBottom - WALLET.bottom * fh);
      fx = (w - fw) / 2;
      // Clear the copy column: move right until the wallet starts past the copy, never past the right edge.
      const walletLeft = fx + WALLET.left * fw;
      if (c.top - r.top < fy + WALLET.bottom * fh && walletLeft < copyRight) {
        fx = Math.min(fx + (copyRight - walletLeft), w - PAD - WALLET.right * fw);
        fx = Math.max(fx, (w - fw) / 2);
      }
    } else {
      fw = bw; fh = bh; fx = bx; fy = h * 0.33 - fh / 2;
    }
    const k = fw / bw;
    const dx = fx + fw / 2 - (bx + bw / 2);
    const dy = fy + fh / 2 - (by + bh / 2);
    finalTransform = `translate3d(${dx}px, ${dy}px, 0) scale(${k})`;
    const base = `inset:auto;left:${bx}px;top:${by}px;width:${bw}px;height:${bh}px;object-fit:fill;transform-origin:50% 50%;`;
    media.forEach(m => { m.style.cssText = base; });
    section.classList.toggle('sk-scroll-hero--inset', fx > 0.5 || fy > 0.5 || fx + fw < w - 0.5 || fy + fh < h - 0.5);
    apply(false);
  };
  // Sets the transform for the current phase; a resize never animates.
  const apply = animate => {
    const t = phase === 'intro' ? 'translate3d(0, 0, 0)' : finalTransform;
    media.forEach(m => {
      m.style.transition = animate ? m.style.transition : 'none';
      m.style.transform = t;
    });
  };
  const glide = () => {
    if (phase !== 'intro') return;
    phase = 'glide';
    const left = Math.max(0.3, (video.duration || 6.5) - video.currentTime);
    media.forEach(m => { m.style.transition = `transform ${left}s cubic-bezier(.45, 0, .55, 1)`; m.style.willChange = 'transform'; });
    apply(true);
  };
  const settle = () => { phase = 'final'; apply(false); };

  measure();
  addEventListener('resize', measure, { passive: true });
  addEventListener('load', measure);
  landscape.addEventListener('change', measure);

  if (!video || !motion()) return;

  const cue = parseFloat(video.dataset.menuCue) || 3.1;
  const glideAt = parseFloat(video.dataset.glideStart) || 4.0;
  const menu = () => root.classList.add('sk-hero-menu');
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    menu();
    if (phase === 'intro') settle(); // skip, error, safety net: straight to the final framing
    else phase = 'final';
    root.classList.add('sk-hero-done');
    if (window.SekageHeroUnlock) window.SekageHeroUnlock();
    if (window.SekageIntroSeen && root.classList.contains('sk-hero-motion')) window.SekageIntroSeen(); // played (or cut short): once per visitor
  };
  window.SekageHeroFinish = finish;

  // Cues run off the film's own clock (frame callbacks where supported, timeupdate otherwise).
  const tick = () => {
    const t = video.currentTime;
    if (t >= cue) menu();
    if (t >= glideAt) glide();
    if (phase === 'intro' && !video.paused && 'requestVideoFrameCallback' in video) video.requestVideoFrameCallback(tick);
  };
  video.addEventListener('playing', tick);
  video.addEventListener('timeupdate', tick);
  video.addEventListener('ended', finish);
  video.addEventListener('error', finish);
  if (video.ended) finish();

  // Watchdog: if the film stops advancing for 3 s (stalled, never started), show everything and unlock.
  let last = -1, still = 0;
  const watch = setInterval(() => {
    if (done) return clearInterval(watch);
    if (!motion()) { clearInterval(watch); settle(); return; } // dropped to the static hero
    if (video.currentTime === last) still += 500; else { still = 0; last = video.currentTime; }
    if (still >= 3000) {
      clearInterval(watch);
      if (video.currentTime === 0) root.classList.remove('sk-hero-motion'); // never started: static hero
      finish();
    }
  }, 500);

  // Hard fallback: if the film has not started by 6 s (iOS Low Power Mode refuses autoplay, slow network), drop to the
  // static hero and release the scroll lock. A film that is playing is left alone; it ends or the watchdog catches a stall.
  setTimeout(() => {
    if (done || (video.currentTime > 0 && !video.paused)) return;
    root.classList.remove('sk-hero-motion');
    finish();
  }, 6000);
})();
